import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAccountShort, IAddressShort, ISubdivisionShort, ISupplyShort, IUserShort } from 'common/models';
import { DEFAULT_VALUE_0 } from 'common/config';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { uploadExcelFile } from 'common/helpers/file';
import { AppDispatch, RootState } from 'app/store';
import {
  IDistributeRequestPayload,
  IRequest,
  ICancelRequestPayload,
  IRequestListPayload,
  IRequestDistribution,
  IRequestListItem,
  IRequestMembers,
  IRequestStats,
} from 'entities/Request/Request.models';
import { requestTransport } from 'entities/Request/Request.transport';

interface IState {
  request: IRequest | null;
  requestList: IRequestListItem[];
  requestCount: number;
  isRequestLoading: boolean;
  requestBuyers: IAccountShort[];
  requestSubdivisions: ISubdivisionShort[];
  requestUsers: IUserShort[];
  requestAddresses: IAddressShort[];
  requestStats: IRequestStats;
  requestMembers: IRequestMembers | null;
  requestDistributions: IRequestDistribution[];
}

const initialState: IState = {
  request: null,
  requestList: [],
  requestCount: DEFAULT_VALUE_0,
  isRequestLoading: false,
  requestBuyers: [],
  requestSubdivisions: [],
  requestUsers: [],
  requestAddresses: [],
  requestStats: {
    createdCount: DEFAULT_VALUE_0,
    processingCount: DEFAULT_VALUE_0,
    unread: DEFAULT_VALUE_0,
  },
  requestMembers: null,
  requestDistributions: [],
};

export const requestSlice = createSlice({
  name: 'request',
  initialState,
  reducers: {
    setRequest(state, action: PayloadAction<IRequest | null>) {
      state.request = action.payload;
    },
    setRequestList(state, action: PayloadAction<IRequestListItem[]>) {
      state.requestList = action.payload;
    },
    setRequestListChunk(state, action: PayloadAction<IRequestListItem[]>) {
      state.requestList.push(...action.payload);
    },
    setRequestCount(state, action: PayloadAction<number>) {
      state.requestCount = action.payload;
    },
    setIsRequestLoading(state, action: PayloadAction<boolean>) {
      state.isRequestLoading = action.payload;
    },
    setRequestBuyers(state, action: PayloadAction<IAccountShort[]>) {
      state.requestBuyers = action.payload;
    },
    setRequestSubdivisions(state, action: PayloadAction<ISubdivisionShort[]>) {
      state.requestSubdivisions = action.payload;
    },
    setRequestUsers(state, action: PayloadAction<IUserShort[]>) {
      state.requestUsers = action.payload;
    },
    setRequestAddresses(state, action: PayloadAction<IAddressShort[]>) {
      state.requestAddresses = action.payload;
    },
    setRequestStats(state, action: PayloadAction<IRequestStats>) {
      state.requestStats = action.payload;
    },
    setRequestMembers(state, action: PayloadAction<IRequestMembers | null>) {
      state.requestMembers = action.payload;
    },
    setRequestDistributions(state, action: PayloadAction<IRequestDistribution[]>) {
      state.requestDistributions = action.payload;
    },
    updateRequestSupplies(state, action: PayloadAction<ISupplyShort[]>) {
      if (!state.request) {
        return;
      }

      state.request = {
        ...state.request,
        supplies: state.request.supplies.map((item) => {
          const newSupply = action.payload.find((i) => i.id === item.id);

          return newSupply ? newSupply : item;
        }),
      };
    },
  },
});

export const {
  setRequest,
  setRequestList,
  setRequestListChunk,
  setRequestCount,
  setIsRequestLoading,
  setRequestBuyers,
  setRequestSubdivisions,
  setRequestUsers,
  setRequestAddresses,
  setRequestStats,
  setRequestMembers,
  setRequestDistributions,
  updateRequestSupplies,
} = requestSlice.actions;

export const selectRequest = (state: RootState) => state.request.request;
export const selectRequestList = (state: RootState) => state.request.requestList;
export const selectRequestCount = (state: RootState) => state.request.requestCount;
export const selectIsRequestLoading = (state: RootState) => state.request.isRequestLoading;
export const selectRequestBuyers = (state: RootState) => state.request.requestBuyers;
export const selectRequestSubdivisions = (state: RootState) => state.request.requestSubdivisions;
export const selectRequestUsers = (state: RootState) => state.request.requestUsers;
export const selectRequestAddresses = (state: RootState) => state.request.requestAddresses;
export const selectRequestStats = (state: RootState) => state.request.requestStats;
export const selectRequestMembers = (state: RootState) => state.request.requestMembers;
export const selectRequestDistributions = (state: RootState) => state.request.requestDistributions;

export const requestReducer = requestSlice.reducer;

export const getRequestList = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.get(payload);
    dispatch(setRequestList(response.data));
    dispatch(setRequestCount(response.count));
    return response.data;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestListChunk = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.get(payload);
    dispatch(setRequestListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getById(id);
    dispatch(setRequest(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const cancelRequest = (payload: ICancelRequestPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    await requestTransport.cancel(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestBuyers = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getBuyers(payload);
    dispatch(setRequestBuyers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestSubdivisions = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getSubdivisions(payload);
    dispatch(setRequestSubdivisions(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestUsers = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getUsers(payload);
    dispatch(setRequestUsers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestAddresses = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getAddresses(payload);
    dispatch(setRequestAddresses(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestStats = (payload: IRequestListPayload) => async (dispatch: AppDispatch) => {
  try {
    const response = await requestTransport.getStats(payload);
    dispatch(setRequestStats(response));
  } catch (error) {
    axiosErrorHandler(error);
  }
};

export const getRequestMembers = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getMembers(id);
    dispatch(setRequestMembers(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const uploadRequestExcel = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.uploadExcel(id);
    uploadExcelFile(response, `request_${id}`);
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const getRequestDistributions = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.getDistributions(id);
    dispatch(setRequestDistributions(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};

export const distributeRequest = (payload: IDistributeRequestPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestLoading(true));

  try {
    const response = await requestTransport.distributeRequest(payload);
    dispatch(setRequestDistributions(response.data));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestLoading(false));
  }
};
